import { DELAY } from '../constants/GeneralConstants';
const SCROLLING_BEHAVIOR_SMOOTH = 'smooth';
const SCROLLING_BEHAVIOR_AUTO = 'auto';
const SCROLL_DEBOUNCE = 100;
const FORM_ERROR_ELEMENT_OFFSET_DESKTOP = 50;
const FORM_ERROR_ELEMENT_OFFSET_MOBILE = 150;
export const STICKY_BAR_OFFSET_DESKTOP = 80;
export const NAVIGATION_MENU_OFFSET = 110;
export class ScrollUtil {
    /**
     * Scroll to an element, with the option to make it smooth
     *
     * @param {HTMLElement} element element
     * @param {boolean} smooth smoothy scroll
     * @param {ScrollLogicalPosition} verticalAlignment vertical alignment scroll
     * @param {ScrollLogicalPosition} horizontalAlignment horizontal alignment scroll
     */
    static scrollToElement(element, smooth = false, verticalAlignment = 'start', horizontalAlignment = 'nearest') {
        if (element) {
            const scrollBehavior = smooth ? SCROLLING_BEHAVIOR_SMOOTH : SCROLLING_BEHAVIOR_AUTO;
            element.scrollIntoView({
                behavior: scrollBehavior,
                block: verticalAlignment,
                inline: horizontalAlignment,
            });
        }
    }
    /**
     * Scroll to the top of the page, with the option to make it smooth
     *
     * @param {boolean} smooth smoothy scroll
     */
    static scrollToTop(smooth = false) {
        const bodySelect = document.getElementsByTagName('body');
        if (bodySelect === null || bodySelect === void 0 ? void 0 : bodySelect[0]) {
            ScrollUtil.scrollToElement(bodySelect[0], smooth);
        }
    }
    /**
     * Scroll to the position, with the option to make it smooth
     *
     * @param {number} top top position
     * @param {number} left left position
     * @param {boolean} smooth smoothy scroll
     * @param {Function} onScrollFinish will be called after scrolling is completed
     * @param {HTMLElement} container scroll this element instead of the window (e.g. a modal/bottom sheet's own scrollable content)
     */
    static scrollToPosition(top = 0, left = 0, smooth = true, onScrollFinish, container) {
        const scrollBehavior = smooth ? SCROLLING_BEHAVIOR_SMOOTH : SCROLLING_BEHAVIOR_AUTO;
        const roundedTop = Math.floor(top);
        const scrollTarget = container !== null && container !== void 0 ? container : window;
        const getCurrentScrollTop = () => (container ? container.scrollTop : window.scrollY);
        if (onScrollFinish) {
            let isFinished = false;
            const finish = () => {
                if (isFinished)
                    return;
                isFinished = true;
                scrollTarget.removeEventListener('scroll', onScroll);
                onScrollFinish();
            };
            const onScroll = this.debounce(() => {
                if (roundedTop === Math.floor(getCurrentScrollTop())) {
                    finish();
                }
            }, SCROLL_DEBOUNCE);
            scrollTarget.addEventListener('scroll', onScroll);
            onScroll();
            // safety net: guarantees onScrollFinish is always eventually called, even if the
            // target scroll position is never reached (fe. user scrolls itself during scroll,
            // or the container can't scroll that far)
            setTimeout(finish, DELAY.DELAY_3000);
        }
        if (container) {
            container.scrollTo({ top: roundedTop, left, behavior: scrollBehavior });
        }
        else {
            window.scroll({ top: roundedTop, left, behavior: scrollBehavior });
        }
    }
    /**
     * Finds the nearest scrollable ancestor of an element, if any. Used to detect when an
     * element lives inside its own scroll container (e.g. a modal or bottom sheet) rather
     * than the page itself - which may have its scroll locked (see useLockBodyScroll).
     */
    static findScrollableAncestor(element) {
        let parent = element.parentElement;
        while (parent && parent !== document.body) {
            const { overflowY } = window.getComputedStyle(parent);
            if ((overflowY === 'auto' || overflowY === 'scroll') && parent.scrollHeight > parent.clientHeight) {
                return parent;
            }
            parent = parent.parentElement;
        }
        return null;
    }
    static scrollToErrorElement(element, isDesktop, onScrollFinish) {
        if (!element) {
            return undefined;
        }
        const offset = isDesktop ? FORM_ERROR_ELEMENT_OFFSET_DESKTOP : FORM_ERROR_ELEMENT_OFFSET_MOBILE;
        const elementTop = element.getBoundingClientRect().top;
        const container = this.findScrollableAncestor(element);
        if (container) {
            const relativeTop = elementTop - container.getBoundingClientRect().top;
            const top = container.scrollTop + relativeTop - offset;
            relativeTop < 0 ? this.scrollToPosition(top, 0, true, onScrollFinish, container) : onScrollFinish === null || onScrollFinish === void 0 ? void 0 : onScrollFinish();
            return;
        }
        const top = elementTop + window.scrollY - offset;
        elementTop < 0 ? this.scrollToPosition(top, 0, true, onScrollFinish) : onScrollFinish === null || onScrollFinish === void 0 ? void 0 : onScrollFinish();
    }
    static getScrollingOffset(isDesktop, isTablet, withStickyBar) {
        if (isDesktop && withStickyBar) {
            return STICKY_BAR_OFFSET_DESKTOP;
        }
        if (isTablet) {
            return NAVIGATION_MENU_OFFSET;
        }
        return 0;
    }
    static debounce(func, wait) {
        let timeout;
        return function (...args) {
            if (timeout) {
                clearTimeout(timeout);
            }
            timeout = setTimeout(() => {
                func.apply(this, args);
            }, wait);
        };
    }
}
